import axios from "axios";
import { track } from "@/utils/events/tracker";
import { logAxiosError } from "./utils";
import { axiosGet } from "./axiosWrapper";

// Checks Supabase's public status page API for overall platform health
// Reference: https://status.supabase.com/api/v2/status.json
const checkSupabaseStatus = async (): Promise<boolean> => {
  try {
    // If there was a network error that wasn't on our end this would throw an
    // error.  Even in a supabase outage this should still work (the values of
    // the returned json would just be indicating the outage)
    await axios.get("https://status.supabase.com/api/v2/status.json", {});

    return true;
  } catch (e) {
    logAxiosError("Error checking Supabase platform status:", e);
    track("SUPABASE_STATUS_CHECK_ERROR", { error: `${e}` });
    return false;
  }
};

export const checkRedisHealth = async (
  canvasId: string,
  supabaseClient: any,
): Promise<{
  confirmedRedisDown: boolean;
  anyCheckFailed: boolean;
}> => {
  if (!supabaseClient) {
    return {
      confirmedRedisDown: false,
      anyCheckFailed: false,
    };
  }

  // Single-endpoint reachability check
  const checkEndpoint = async (
    url: string,
  ): Promise<{ ok: boolean; status?: number }> => {
    try {
      const res = await axiosGet(url, {
        errorMessage: "Error checking redis health",
        showToast: false, // Don't show toast for health checks
        throwOnError: true, // Don't throw, we want to handle it
      });

      if (!res?.data) {
        throw Error("No Response");
      }

      return { ok: res.status < 400, status: res.status };
    } catch (e) {
      logAxiosError(`Redis health check failed for ${url}:`, e);
      return { ok: false, status: (e as any)?.response?.status };
    }
  };

  const baseUrl = import.meta.env.VITE_BACKEND_BASE_URL;
  let attempts = 0;
  let anyCheckFailed = false;
  let general: { ok: boolean; status?: number } = { ok: false };
  let canvas: { ok: boolean; status?: number } = { ok: false };
  let redisIsUp = false;

  // Single loop: perform both checks per attempt, exit early on success
  while (attempts < 3) {
    [general, canvas] = await Promise.all([
      checkEndpoint(`${baseUrl}/redis-health`),
      checkEndpoint(`${baseUrl}/redis-health/${canvasId}`),
    ]);
    redisIsUp = general.ok && canvas.ok;

    anyCheckFailed = anyCheckFailed || !redisIsUp;
    attempts++;

    if (redisIsUp) break;
    if (attempts < 3) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  // Only confirm Redis is down if Supabase platform is operational.  This helps
  // avoid blaming Redis when the user is having a general network issue on
  // their end.
  let confirmedRedisDown = false;
  if (!redisIsUp) {
    const supabaseOk = await checkSupabaseStatus();
    confirmedRedisDown = !redisIsUp && supabaseOk;

    // Consolidated logging at the end so we can include supabase info
    track("REDIS_HEALTH_ERROR", {
      canvasId,
      generalHealthy: general.ok,
      canvasHealthy: canvas.ok,
      generalStatus: general.status,
      canvasStatus: canvas.status,
      attempts,
      supabaseOk,
      confirmedRedisDown,
    });
  }

  return {
    confirmedRedisDown,
    anyCheckFailed,
  };
};
